import { useRef, useEffect, useLayoutEffect, useCallback, useState, useMemo } from "react";
import useThrottle from "./useThrottle";
import useMeasure from "react-use-measure";
import { ResizeObserver } from "@juggle/resize-observer";

const measureOptions = { polyfill: ResizeObserver };
const defaultOption = {
  preloadOffset: 0, // offset from bottom
  throttleTime: 250,
  delayUnblock: 1000,
  dirDownOnly: true,
  defaultStart: true,
};
/*
 * return [
 *   containerRef,
 *   scrollableRef,
 *   functional,     // warrantee all function is not change
 * ]
 */
const useLoadMore = (callback, options) => {
  const optionsRef = useRef(Object.assign({}, defaultOption, options));
  const callbackRef = useRef(callback);
  const blocked = useRef(!optionsRef.current.defaultStart);
  const [terminated, setTerminated] = useState(!optionsRef.current.defaultStart);
  const [containerMeasureRefCallback, containerRect] = useMeasure(measureOptions);
  const [scrollableMeasureRefCallback, scrollableRect] = useMeasure(measureOptions);
  const containerRef = useRef();
  const scrollableRef = useRef();
  const prevPositionRef = useRef(0);

  const containerRefCallback = useCallback(
    (node) => {
      containerMeasureRefCallback(node);
      containerRef.current = node;
    },
    [containerMeasureRefCallback]
  );

  const scrollableRefCallback = useCallback(
    (node) => {
      scrollableMeasureRefCallback(node);
      scrollableRef.current = node;
    },
    [scrollableMeasureRefCallback]
  );

  const block = useCallback(() => (blocked.current = true), []);
  const unblock = useCallback(() => setTimeout(() => (blocked.current = false), optionsRef.current.delayUnblock), []);
  const start = useCallback(() => {
    setTerminated(false);
    unblock();
  }, [unblock]);
  const stop = useCallback(() => setTerminated(true), []);

  const [handleScroll] = useThrottle(
    () => {
      function isScrollDirectionAllowed(currentScrollTopPosition) {
        let allow = false;
        if (optionsRef.current.dirDownOnly) {
          allow = prevPositionRef.current > currentScrollTopPosition;
        }
        prevPositionRef.current = currentScrollTopPosition;
        return allow;
      }

      if (containerRef.current && !blocked.current && !terminated) {
        const container = containerRef.current;
        const scrollable = scrollableRef?.current ?? document.documentElement;
        const sceneHeight = scrollableRef?.current ? scrollableRect.height : window.innerHeight;
        const sceneOffsetTop = scrollable.scrollTop;

        if (isScrollDirectionAllowed(sceneOffsetTop)) return;

        const sceneOffsetBottom = sceneHeight + sceneOffsetTop;

        // find bottom line position of container in scrollable
        const containerHeight = containerRect.height;
        const containerOffsetTopInScrollable = container.offsetTop;
        const bottomOfContainerInScrollable = containerOffsetTopInScrollable + containerHeight;
        const bottomLineIndicator = bottomOfContainerInScrollable - optionsRef.current.preloadOffset;

        // console.log({
        //   containerRect,
        //   containerHeight,
        //   sceneHeight,
        //   sceneOffsetBottom,
        //   containerOffsetTopInScrollable,
        //   bottomOfContainerInScrollable,
        //   bottomLineIndicator,
        //   sceneOffsetTop,
        //   loadMore: sceneOffsetBottom >= bottomLineIndicator,
        // })

        if (sceneOffsetBottom < bottomLineIndicator) {
          return;
        } else {
          callbackRef.current([block, unblock]);
        }
      }
    },
    optionsRef.current.throttleTime,
    [block, unblock, terminated, containerRect.height, scrollableRect.height]
  );

  useLayoutEffect(() => void (callbackRef.current = callback), [callback]);
  useLayoutEffect(() => void (optionsRef.current = Object.assign({}, defaultOption, options)), [options]);

  useEffect(() => {
    if (!terminated) {
      const node = scrollableRef.current ?? window;
      node.addEventListener("scroll", handleScroll);
      return () => node.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, terminated]);

  const functional = useMemo(() => ({ block, unblock, start, stop }), [block, unblock, start, stop]);

  return [containerRefCallback, scrollableRefCallback, functional];
};

export default useLoadMore;
